if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        // App blocks and recommendation shells may temporarily render an empty
        // <product-form>. They are not commerce owners and must not abort the
        // real PDP forms that follow.
        if (!this.form) return;
        const variantIdInput = this.variantIdInput;
        if (!variantIdInput) return;
        variantIdInput.disabled = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span') || this.submitButton;
        this.boundSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.boundSubmitHandler);
        // Capture the visible CTA before optional bundle/subscription modules can
        // replace Ajax submission with form.submit(), which bypasses submit events.
        this.submitButton.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.boundSubmitHandler(event);
        }, true);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              })
                .then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
                .catch((error) => {
                  console.error('[ProductForm] Cart update subscriber failed', error);
                });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartAndReconcile(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCartAndReconcile(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      renderCartAndReconcile(response) {
        this.cart.renderContents(response);

        // Shopify can return /cart/add.js section HTML before an automatic
        // discount allocation is reflected in Liquid. Normal products can
        // reconcile immediately. A form carrying an explicit campaign code
        // gets two bounded reconciliation points around Shopify/PPCSE's
        // asynchronous allocation window. There is no open-ended polling loop.
        const campaignCode = this.form.querySelector('[data-cv6-campaign-code]')?.value?.trim();
        const campaignGraceMs = Number(window.CV6_CAMPAIGN_CART_GRACE_MS || 2500);
        const campaignSettleMs = Number(window.CV6_CAMPAIGN_CART_SETTLE_MS || 6000);
        const variantId = Number(response?.variant_id || response?.id || 0);
        const drawerItems = document.querySelector('cart-drawer-items');
        const ready = campaignCode
          ? new Promise((resolve) => window.setTimeout(resolve, campaignGraceMs))
          : Promise.resolve();
        ready
          .then(async () => {
            try {
              await drawerItems?.onCartUpdate({ cacheBust: true });
              if (window.CosmeauCartDrawer?.refreshSections) {
                window.CosmeauCartDrawer.clearPendingItem?.();
                await window.CosmeauCartDrawer.refreshSections(true);
              }
            } finally {
              if (campaignCode) await this.reconcileCampaignPricing(campaignCode, variantId);
            }
            if (!campaignCode) return;
            await new Promise((resolve) => window.setTimeout(resolve, campaignSettleMs));
            try {
              await drawerItems?.onCartUpdate({ cacheBust: true });
              if (window.CosmeauCartDrawer?.refreshSections) {
                window.CosmeauCartDrawer.clearPendingItem?.();
                await window.CosmeauCartDrawer.refreshSections(true);
              }
            } finally {
              await this.reconcileCampaignPricing(campaignCode, variantId);
            }
          })
          .catch((error) => {
            console.error('[ProductForm] Cart drawer reconciliation failed', error);
          });
      }

      async reconcileCampaignPricing(campaignCode, variantId) {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Campaign cart reconciliation failed (${response.status})`);
        const cart = await response.json();
        const line = cart.items?.find((item) => Number(item.variant_id) === Number(variantId));
        const allocation = line?.line_level_discount_allocations?.find(
          (entry) => entry?.discount_application?.title === campaignCode
        );
        if (!line || !allocation) return false;

        const locale = document.documentElement.lang || 'nl-NL';
        const money = (minor) => new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: cart.currency || 'EUR',
        }).format(Number(minor || 0) / 100);
        const row = [...document.querySelectorAll('tr.cart-item[data-line-key]')]
          .find((candidate) => candidate.dataset.lineKey === line.key)
          || document.querySelector(`[data-quantity-variant-id="${line.variant_id}"]`)?.closest('tr.cart-item');

        if (row) {
          const priceWrapper = row.querySelector('.cart-item__price-wrapper');
          if (priceWrapper) {
            const discounted = document.createElement('div');
            discounted.className = 'cart-item__discounted-prices';
            const oldPrice = document.createElement('s');
            oldPrice.className = 'cart-item__old-price price price--end';
            oldPrice.textContent = money(line.original_line_price);
            const finalPrice = document.createElement('span');
            finalPrice.className = 'price price--end';
            finalPrice.textContent = money(line.final_line_price);
            discounted.append(oldPrice, finalPrice);
            priceWrapper.replaceChildren(discounted);
          }

          const discounts = row.querySelector('.cart-item__details > .discounts');
          if (discounts && ![...discounts.children].some((node) => node.textContent.trim() === campaignCode)) {
            const item = document.createElement('li');
            item.className = 'discounts__discount';
            item.textContent = campaignCode;
            discounts.append(item);
          }
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.dataset.cartTotalMinor = String(cart.total_price);
        document.querySelectorAll('.cosmeau-cart__checkout-total strong').forEach((node) => {
          node.textContent = money(cart.total_price);
        });
        document.querySelectorAll('.totals__total-value').forEach((node) => {
          node.textContent = money(cart.total_price);
        });
        const subtotal = document.querySelector('.cosmeau-cart__footer-metrics span:first-child b');
        if (subtotal) subtotal.textContent = money(cart.original_total_price);
        const discountArea = document.querySelector('.cosmeau-cart__discount-area');
        if (discountArea && ![...discountArea.querySelectorAll('.cosmeau-cart__discount-chip')]
          .some((node) => node.textContent.trim().includes(campaignCode))) {
          const chip = document.createElement('div');
          chip.className = 'cosmeau-cart__discount-chip is-applied';
          chip.textContent = campaignCode;
          discountArea.prepend(chip);
        }
        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
